import { FolderAddFilled, MedicineBoxFilled } from '@ant-design/icons';
import { Button, Card, Typography } from 'antd';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CALENDAR_BARS, IBarConfig, ROLLING_BARS } from './barConfig';

import { BRAND_COLORS } from '../../../../constants/styles';
import { IDonorPipelineStats } from '../../../../hooks/useDonorPipelineStats';
import { DateRangeType } from '../../../../types/phlebotomist-dashboard';
import { RadialBarChart } from '../../../charts/RadialBarChart';
import { STATUS_COLORS } from '../../constants';

const { Text } = Typography;

export interface INewRegistrationsChartProps {
  periodType: 'rolling' | 'calendar';
  onPeriodTypeChange: (type: 'rolling' | 'calendar') => void;
  pipelineStats: IDonorPipelineStats | null;
  pipelineLoading: boolean;
  getRegistrationCount: (period: DateRangeType) => number;
  getRegistrationData: (
    period: DateRangeType
  ) => { startDate?: string; endDate?: string } | null;
  getYesterdayDonors: () => number;
  getTodayDonors: () => number;
  getYesterdayLabel: () => string;
  onBarClick: (barName: IBarConfig['name']) => void;
  onNewDonorsClick: () => void;
}

export const NewRegistrationsChart: FC<INewRegistrationsChartProps> = ({
  periodType,
  onPeriodTypeChange,
  pipelineStats,
  pipelineLoading,
  getRegistrationCount,
  getRegistrationData,
  getYesterdayDonors,
  getTodayDonors,
  getYesterdayLabel,
  onBarClick,
  onNewDonorsClick,
}) => {
  const { t } = useTranslation('dashboard.RecruiterDashboard.newRegistrations');
  const [showTodayDonors, setShowTodayDonors] = useState(false);

  const isRolling = periodType === 'rolling';
  const bars = isRolling ? ROLLING_BARS : CALENDAR_BARS;

  const chartData = useMemo(
    () => [
      {
        name: 'yesterday',
        label: '',
        count: 0,
        barValue: 0,
        color: 'transparent',
      },
      ...bars.map((b) => ({
        name: b.name,
        label: t(`barLabels.${isRolling ? 'rolling' : 'calendar'}.${b.name}`),
        count: getRegistrationCount(b.period),
        barValue: getRegistrationCount(b.period) * b.scale,
        color: b.color,
      })),
    ],
    [bars, isRolling, getRegistrationCount, t]
  );

  const legendClickConfigs = useMemo(() => {
    const fmtRange = (period: DateRangeType): string => {
      const d = getRegistrationData(period);
      if (!d?.startDate || !d?.endDate) return '';
      const s = new Date(d.startDate);
      const e = new Date(d.endDate);
      const opts: Intl.DateTimeFormatOptions = {
        month: 'short',
        day: 'numeric',
      };
      if (s.getFullYear() !== e.getFullYear()) opts.year = 'numeric';
      return `${s.toLocaleDateString('en-US', opts)} – ${e.toLocaleDateString(
        'en-US',
        { month: 'short', day: 'numeric', year: 'numeric' }
      )}`;
    };

    return Object.fromEntries(
      bars.map((b) => [
        b.name,
        {
          onClick: () => onBarClick(b.name),
          tooltip: (
            <span>
              <strong>{fmtRange(b.period)}</strong>
              <br />
              {t(`barTooltips.${b.name}.${isRolling ? 'rolling' : 'calendar'}`)}
            </span>
          ),
        },
      ])
    );
  }, [bars, isRolling, getRegistrationData, onBarClick]);

  const toggleBtnStyle = (
    active: boolean,
    color: string
  ): React.CSSProperties => ({
    backgroundColor: active ? color : undefined,
    color: active ? '#fff' : color,
    borderColor: color,
    fontWeight: 600,
    fontSize: 12,
  });

  const yesterdayLabel = getYesterdayLabel();
  const newDonorsLabel = showTodayDonors
    ? t('todayLabel')
    : yesterdayLabel === 'Yesterday'
    ? t('yesterdayLabel')
    : yesterdayLabel;

  return (
    <Card size="small" bordered={false} style={{ height: '100%' }}>
      <Text style={{ color: '#2f3a45', display: 'block', textAlign: 'center' }}>
        {t('title')}
      </Text>

      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          gap: 4,
          marginTop: 8,
        }}
      >
        <Button
          size="small"
          style={toggleBtnStyle(isRolling, BRAND_COLORS.primary.deepNavy)}
          onClick={() => onPeriodTypeChange('rolling')}
        >
          {t('rolling')}
        </Button>
        <Button
          size="small"
          style={toggleBtnStyle(!isRolling, STATUS_COLORS.good.border)}
          onClick={() => onPeriodTypeChange('calendar')}
        >
          {t('calendar')}
        </Button>
      </div>

      <div style={{ height: 420, marginTop: 8, paddingTop: 35 }}>
        <div
          style={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {pipelineLoading ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              {t('loading')}
            </div>
          ) : (
            <RadialBarChart
              data={chartData}
              height={420}
              spread={45}
              fill={0.8}
              innerRadius={1}
              flip={false}
              swapLabels={true}
              gap={0}
              marginLeft={40}
              activeDonorShow={true}
              activeDonorLeft={0}
              activeDonorBottom={120}
              activeDonorNumber={pipelineStats?.activeDonorCount}
              newDonorsYesterday={
                showTodayDonors ? getTodayDonors() : getYesterdayDonors()
              }
              newDonorsLabel={newDonorsLabel}
              showingToday={showTodayDonors}
              onNewDonorsToggle={() => setShowTodayDonors((v) => !v)}
              activeDonorIcon={<MedicineBoxFilled />}
              newDonorsYesterdayIcon={<FolderAddFilled />}
              cornerRadius={6}
              centered={true}
              legendClickConfigs={legendClickConfigs}
              onNewDonorsClick={onNewDonorsClick}
              newDonorsClickTooltip={t('clickTooltip', { yesterdayLabel })}
            />
          )}
        </div>
      </div>
    </Card>
  );
};
